import qs from 'qs'
import { useQuery } from '@tanstack/react-query'
import { fetchWithAlerts } from '~/common/fetchUtils'
import { getParameterString } from '~/common/query'
import { VndlyAlert } from '~/common/VndlyAlert'

import { formatDataDate } from '~/common/dateUtils'
import fetch from '../../common/fetch'
import { BASE_URL } from './constants'
import { withPagination } from './utils'

const JOBS_LIMIT = 10
const WORKERS_LIMIT = 20
const get = async (sowId, versionId, parameters) => {
  const parametersWithLimit = { ...parameters, limit: JOBS_LIMIT }
  const paramString = getParameterString(parametersWithLimit)
  const response = await fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/?${paramString}`)
  return withPagination(response, JOBS_LIMIT)
}

const getAll = async (sowId, versionId, parameters) => {
  const parametersWithLimit = { limit: JOBS_LIMIT, ...parameters }
  const paramString = getParameterString(parametersWithLimit)
  const response = await fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/?${paramString}`)
  return response
}

const getSowJob = (sowId, versionId, jobId, params = {}) => {
  const paramString = getParameterString(params)
  return fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/?${paramString}`)
}

const getJobFormConfig = (sowId, versionId) =>
  fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/form-config`)

const getSummaryData = async (sowId, versionId) =>
  fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/summary`)

const getJobApplications = async (sowId, versionId, jobId) =>
  fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/job-applications`)

/**
 *
 * @param sowId
 * @param versionId
 * @param jobId
 * @returns {Promise<JobApplyModalDetail>}
 */
const getJobDetailApply = async (sowId, versionId, jobId) =>
  fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/get-sow-detail-apply`)

const create = async (sowId, versionId, data) => fetch.post(`${BASE_URL}${sowId}/version/${versionId}/job/`, data)

const save = async (sowId, versionId, data) =>
  fetch.put(`${BASE_URL}${sowId}/version/${versionId}/job/${data.id}/`, data)

const destroy = (sowId, versionId, jobId) => fetch.delete(`${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/`)

const close = (sowId, versionId, jobId, closeUnfilledPositions) =>
  fetch.post(`${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/close/`, {
    should_close_positions: closeUnfilledPositions
  })

const getCandidates = async (vendorId, parameters) => {
  const parametersWithLimit = { ...parameters, limit: WORKERS_LIMIT }
  const paramString = getParameterString(parametersWithLimit)
  const response = await fetch.get(`/api/applicable-candidates-by-vendor/${vendorId}/?${paramString}`)
  return withPagination(response, WORKERS_LIMIT)
}

const getJobWorkers = async (sowId, versionId, jobId, parameters) => {
  const parametersWithLimit = { ...parameters, limit: WORKERS_LIMIT }
  const response = await fetchWithAlerts(
    fetch.get,
    `${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/job-workers/?${qs.stringify(parametersWithLimit, {
      skipNulls: true,
      arrayFormat: 'comma',
      serializeDate: d => formatDataDate(d)
    })}`
  )
  return { ...response, defaultWwst: response?.default_wwst ?? null }
}

const getSowWorkers = async (sowId, versionId, parameters) => {
  const limit = parameters?.limit ?? WORKERS_LIMIT
  const parametersWithLimit = { ...parameters, limit }
  const response = await fetchWithAlerts(
    fetch.get,
    `${BASE_URL}${sowId}/version/${versionId}/sow-workers/?${qs.stringify(parametersWithLimit, {
      skipNulls: true,
      arrayFormat: 'comma',
      serializeDate: d => formatDataDate(d)
    })}`
  )
  return response
}

const getSowJobWorker = async (sowId, versionId, jobId, candidateId) =>
  fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/job-workers/${candidateId}`)

const getChecklists = async (sowId, versionId, jobId) =>
  fetchWithAlerts(fetch.get, `${BASE_URL}${sowId}/version/${versionId}/job/${jobId}/checklists/`)

const getWorkerChecklists = async params => {
  const { status = null, ...rest } = params
  let queryString = qs.stringify(rest)
  if (status) {
    queryString += status.split(',').reduce((value, status) => `${value}&status=${status}`, '')
  }
  return fetch.get(`/api/v2/job_application_actions/?${queryString}`)
}

const getTotalChecklists = async params => {
  const { ...rest } = params

  const queryString = qs.stringify(rest)

  return fetch.get(`/api/v2/job_application_actions/get_total_counts/?${queryString}`)
}

const applyOffer = async (data, showSuccessMessage = true) => {
  // TODO A failure response actually contains the candidates that failed along with an error message.  Display it.
  const errorMessage = 'One or more candidates failed to be added.  Please contact the VNDLY support team.'
  return fetch
    .post(`/api/v2/apply-offer/`, data)
    .then(response => {
      if (response?.errors) {
        // Having a controlled error onboarding a candidate will not be handled here but any other
        // unexpected behavior
        VndlyAlert.unexpectedError.fire(errorMessage && { text: errorMessage })
      } else if (showSuccessMessage) {
        VndlyAlert.toast.success.fire('Applied Successfully')
      }
      return response
    })
    .catch(() => {
      VndlyAlert.unexpectedError.fire(errorMessage && { text: errorMessage })
      return { error: true }
    })
}

const getResourceManagers = async (businessUnitId, query, context = null) => {
  const contextParam = context ? '&context=' + context : ''
  return fetch.get(`/api/v2/employer_manager/?at_bu=${businessUnitId}&query=${query}&include_inactive=0${contextParam}`)
}

const getResourceManagerById = async (businessUnitId, id, context = null) => {
  const contextParam = context ? '&context=' + context : ''
  return fetch.get(`/api/v2/employer_manager/?at_bu=${businessUnitId}&id=${id}&include_inactive=0${contextParam}`)
}

export const useGetResourceManagersQuery = (businessUnitId, query, context = null) =>
  useQuery({
    queryKey: [`sow/getResourceManagers/${businessUnitId}`, businessUnitId, query],
    queryFn: () => getResourceManagers(businessUnitId, query, context),
    enabled: !!businessUnitId,
    keepPreviousData: true
  })

export const validRMQueryKey = (businessUnitId, rmId) => {
  return [`sow/getResourceManagers/${businessUnitId}`, businessUnitId, rmId]
}

export const useIsValidRmQuery = (businessUnitId, rmId, context = null) =>
  useQuery({
    queryKey: validRMQueryKey(businessUnitId, rmId),
    queryFn: () => getResourceManagerById(businessUnitId, rmId, context),
    enabled: !!businessUnitId && !!rmId,
    staleTime: Infinity
  })

export const sowJobApi = {
  get,
  getAll,
  getJobFormConfig,
  getSowJob,
  create,
  save,
  destroy,
  close,
  getSummaryData,
  getCandidates,
  applyOffer,
  getJobDetailApply,
  getJobApplications,
  getWorkers: getJobWorkers,
  getSowWorkers,
  getResourceManagers,
  getChecklists,
  getSowJobWorker,
  getWorkerChecklists,
  getTotalChecklists
}
